import { useMemo } from 'react';
import useCompanyBrand from './useCompanyBrand';
import { useBrandSource } from '../theme/BrandSourceProvider';
import { CompanyBrand, DEFAULT_BRAND } from '../models/companyBrand';
// item 133: the real Basix logo, shown when the brand source is "Stock Basix"
import basixLogo from '../assets/logo.png';

// item 76 — the brand actually RENDERED, after applying the per-device brand source
// (own / Basix colours + my logo / stock Basix). Use this anywhere the live look is
// derived from the brand (the Chakra theme, the sidebar logo/glow). The raw company
// brand (useCompanyBrand) is still what Settings → Branding edits.
// item 1517 (perf) — memoized on [brand, brandSource]: the spread below minted a FRESH
// object every render for every non-'own' source, which defeated BrandThemeProvider's
// useMemo and rebuilt the entire Chakra theme on every render of the shell.
const useEffectiveBrand = (): { brand: CompanyBrand | undefined } => {
  const { brand } = useCompanyBrand();
  const { brandSource } = useBrandSource();

  const effective = useMemo<CompanyBrand | undefined>(() => {
    if (!brand || brandSource === 'own') return brand;

    // stock Basix colours + font; the company's other fields (name, slogan, logo) ride along
    const basixPalette = {
      primaryColor: DEFAULT_BRAND.primaryColor,
      accentColor: DEFAULT_BRAND.accentColor,
      successColor: DEFAULT_BRAND.successColor,
      warningColor: DEFAULT_BRAND.warningColor,
      errorColor: DEFAULT_BRAND.errorColor,
      fontFamily: DEFAULT_BRAND.fontFamily,
    };

    if (brandSource === 'basixColors') {
      // Basix colours + font, keep the company's own logo (co-brand)
      return { ...brand, ...basixPalette };
    }

    // item 133: stock = full Basix kit — show the REAL Basix logo (not the company's, and
    // not a blank fallback to the company name) + the Basix slogan.
    return {
      ...brand,
      ...basixPalette,
      logoDataUrl: basixLogo,
      slogan: "Let's make business easier.",
    };
  }, [brand, brandSource]);

  return { brand: effective };
};

export default useEffectiveBrand;
